/**
 * 動的グループの一覧(機能キー `hr.groups`)。
 *
 * 条件に当てはまる人を自動で集めるまとまり。**作った時点では何も配らない**
 * (権限セットを付けて、詳細の「初期同期」で明示的に流す)。
 */
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Flex,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import type { DynGroup, GroupRuleNode } from "../../lib/api";
import { jst } from "../../lib/format";
import { useApi } from "../../lib/useApi";
import { GroupRuleEditor } from "./GroupRuleEditor";
import { emptyRule } from "./groupRule";
import { countNote, useGroupFields, useGroups, useInvalidateGroups } from "./groupHooks";
import { errorText, useHrPerms } from "./hooks";

export function GroupsPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { canAdmin } = useHrPerms();
  const invalidate = useInvalidateGroups();
  const [activeOnly, setActiveOnly] = useState(false);
  const [creating, setCreating] = useState(false);
  const [rule, setRule] = useState<GroupRuleNode>(emptyRule());
  const [count, setCount] = useState<number | null>(null);
  const [form] = Form.useForm<{ name: string; description?: string }>();
  const query = useGroups(activeOnly);
  const fields = useGroupFields();

  const preview = useMutation({
    mutationFn: () => api.previewGroup(rule),
    onSuccess: (r) => setCount(r.count),
    onError: (e) => void message.error(errorText(e)),
  });

  const create = useMutation({
    mutationFn: (v: { name: string; description?: string }) =>
      api.createGroup({ name: v.name, description: v.description ?? "", rule }),
    onSuccess: (g) => {
      void message.success("作った(権限セットを付けてから「初期同期」で流す)");
      setCreating(false);
      invalidate();
      navigate(`/hr/groups/${g.id}`);
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const columns: ColumnsType<DynGroup> = [
    {
      title: "名前",
      width: 160,
      dataIndex: "name",
      render: (v: string, g) => <a onClick={() => navigate(`/hr/groups/${g.id}`)}>{v}</a>,
    },
    { title: "説明", width: 320, dataIndex: "description", ellipsis: true },
    { title: "メンバー", dataIndex: "member_count", width: 100 },
    { title: "権限セット", dataIndex: "permission_set_count", width: 110 },
    {
      title: "状態",
      dataIndex: "active",
      width: 110,
      render: (v: boolean) => (v ? <Tag color="green">稼働中</Tag> : <Tag>止めている</Tag>),
    },
    { title: "更新", width: 180, render: (_: unknown, g: DynGroup) => jst(g.updated_at) },
  ];

  return (
    <div>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }} gap={12} wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>
          動的グループ
        </Typography.Title>
        <Space wrap>
          <Checkbox checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)}>
            稼働中だけ
          </Checkbox>
          {canAdmin && (
            <Button
              type="primary"
              onClick={() => {
                form.resetFields();
                setRule(emptyRule());
                setCount(null);
                setCreating(true);
              }}
            >
              グループを作る
            </Button>
          )}
        </Space>
      </Flex>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          条件に当てはまる人を自動で集める。組織・役職・契約形態を直すとその場で入り直る。
          退職・匿名化済みの人は条件に何を書いても入らない。
          作った・条件を変えた時点では何も配らない。権限セットを付けて詳細の「初期同期」を押すと、
          そのときのメンバーぶんの付与タスクが起票される。
        </Typography.Text>
      </Card>

      {!canAdmin && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="閲覧だけできる"
          description="グループの作成・変更は hr:admin だけ。"
        />
      )}

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="グループを取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Table<DynGroup>
        scroll={{ x: "max-content" }}
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isFetching}
        pagination={false}
      />

      {/* 上限で黙って切れないように、表示件数と全件数を出す(Codex 低 2) */}
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {countNote(query.data?.items?.length ?? 0, query.data?.total)}
      </Typography.Text>

      <Modal
        open={creating}
        title="グループを作る"
        okText="作る"
        cancelText="やめる"
        width={860}
        confirmLoading={create.isPending}
        onCancel={() => setCreating(false)}
        onOk={() => void form.validateFields().then((v) => create.mutate(v))}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="名前" rules={[{ required: true, message: "名前は必須" }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="description" label="説明">
            <Input.TextArea rows={2} maxLength={1000} />
          </Form.Item>
        </Form>
        <Typography.Text strong>条件</Typography.Text>
        <div style={{ marginTop: 8 }}>
          {fields.data && (
            <GroupRuleEditor value={rule} fields={fields.data} onChange={setRule} />
          )}
        </div>
        <Space style={{ marginTop: 12 }} wrap>
          <Button loading={preview.isPending} onClick={() => preview.mutate()}>
            件数を確認
          </Button>
          {count !== null && <Typography.Text>いまの条件に当てはまる人: {count} 人</Typography.Text>}
        </Space>
      </Modal>
    </div>
  );
}
